/**
 * HustleReviewsSalesPage.jsx
 * ──────────────────────────
 * Sales page for HustleReviews — the portable, ABN-verified review
 * layer for Australian businesses. Rendered on the bare domain
 * hustlereviews.au via RootRouter, and available in-app at
 * /hustlereviews-sales for preview.
 *
 * Aesthetic mirrors DirectorySalesPage + HustleStadiumSalesPage
 * (cream + Georgia + amber + chapters + StatPills + TierCards +
 * FAQ + print-friendly). Copy targets ~1,900 words.
 *
 * The review-writing UX lives at /reviews and /reviews/respond/:token.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Printer, Sparkles, Star, ShieldCheck,
  Mail, LayoutTemplate, Users, MessageSquare, Building2,
  BadgeCheck, Zap, TrendingUp, Download, HeartHandshake, Lock,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import FounderChatPill from "@/components/FounderChatPill";

const LOGO =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/d6gkfgwo_Local%20Hustle%20Logo%20-%20PLAZA%20LIGHT.png";

const ctaPrimaryStyle = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "12px 22px", borderRadius: 999, border: "none",
  background: "#c2410c", color: "#fff",
  fontWeight: 700, fontSize: 15, cursor: "pointer",
  boxShadow: "0 8px 20px rgba(194,65,12,.22)",
  textDecoration: "none",
};
const ctaSecondaryStyle = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "12px 22px", borderRadius: 999,
  border: "1.5px solid #1a1408", color: "#1a1408",
  fontWeight: 700, fontSize: 15,
  background: "transparent", textDecoration: "none",
  cursor: "pointer",
};

const IMG_HERO =
  "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1400&q=80&auto=format&fit=crop";
const IMG_STARS =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200&q=80&auto=format&fit=crop";
const IMG_RESPONSE =
  "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&q=80&auto=format&fit=crop";
const IMG_WIDGET =
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80&auto=format&fit=crop";

const CAPS = [
  { icon: BadgeCheck, t: "ABN-verified reviewers",
    b: "Every reviewer is linked to a real HustleSuite account. Ghost accounts, review-farm bots and competitor sabotage are auto-filtered before a review ever publishes." },
  { icon: MessageSquare, t: "Public + private responses",
    b: "Reply publicly on the review, or click 'Take it to DMs' to open a private HustleCRM thread. Every conversation is logged, exportable, and yours." },
  { icon: LayoutTemplate, t: "Website widget (WP + Wix + Squarespace)",
    b: "Drop a widget on your website and your five-star reviews render live. Schema.org rich-result markup included — so Google surfaces your stars on the SERP." },
  { icon: Star,       t: "One review, everywhere in the Suite",
    b: "A HustleReviews entry auto-syncs to your HustleDirectory profile, HustlePlate booking page, HustleEats offer card and any WordPress widget you've dropped in." },
  { icon: Mail,       t: "Post-visit review requests",
    b: "One-tap after service: send a personalised review request via email or SMS. Every request includes your logo, the visit details, and a 1-tap star + comment form." },
  { icon: ShieldCheck, t: "Real disputes, real humans",
    b: "If a review looks manipulative, flag it. A HustleSuite trust & safety human reviews the dispute in under 48 hours. No black-box AI arbitration, no walled-garden appeals." },
];

const TIERS = [
  {
    key: "free", name: "Free forever", price: "$0", period: "/forever",
    color: "#1f7a1f",
    cta: { label: "Start collecting reviews", href: "/claim-business?product=reviews&tier=free" },
    blurb: "Every ABN-verified Australian business gets unlimited HustleReviews for free. Own your reviews forever, respond publicly, and export to CSV any time.",
    features: [
      "Unlimited verified reviews",
      "Public + private responses",
      "Star rating on your directory listing",
      "CSV export any time",
      "Review request emails (100/mo)",
      "Basic dispute-review process",
      "Free HustleCRM sub-account (leads flow in)",
    ],
  },
  {
    key: "featured", name: "Reviews Featured", price: "$29", period: "/mo",
    badge: "Recommended", highlight: true, color: "#b08a3e",
    cta: { label: "Go Featured", href: "/claim-business?product=reviews&tier=featured" },
    blurb: "Unlimited review request emails, SMS review requests, embeddable website widget with schema.org rich results, and monthly performance digest.",
    features: [
      "Everything in Free, plus:",
      "Unlimited review request emails",
      "SMS review requests (Aussie-locale)",
      "Embeddable website widget (WP + Wix + Squarespace)",
      "Schema.org rich-result markup (Google stars on SERP)",
      "Monthly performance digest (rating trends + response rate)",
      "Priority dispute-review (24h SLA)",
      "PLATINUM badge across HustleDirectory + HustlePlaza",
    ],
  },
  {
    key: "enterprise", name: "Multi-location", price: "Contact us", period: "",
    color: "#4a3b1f",
    cta: { label: "Book a discovery call", href: "/claim-business?product=reviews&tier=enterprise" },
    blurb: "For groups running 5+ locations under one ABN. Central review-management dashboard, aggregated rating rollups, and a dedicated account manager.",
    features: [
      "Everything in Featured, plus:",
      "Central multi-location dashboard",
      "Aggregated group-wide rating rollup",
      "Custom review request templates per location",
      "White-labelled review request emails",
      "Dedicated success manager (15-min SLA)",
      "Master-services agreement + SOC 2 questionnaire pack",
    ],
  },
];

const HOW = [
  { n: "01", icon: BadgeCheck, t: "Claim your listing (2 min)",
    b: "Enter your ABN. We match it live against the Australian Business Register — verified owners get instant edit access to reviews and responses." },
  { n: "02", icon: Mail, t: "Send your first review request",
    b: "One-tap after service: choose email or SMS, we personalise the request with your logo and the visit details. Diners land on a 1-tap star form." },
  { n: "03", icon: MessageSquare, t: "Respond, learn, publish",
    b: "Reply publicly to every review from your inbox. Reviews auto-sync to your directory profile, WordPress widget, and every HustleSuite module." },
];

const CHAPTERS = [
  {
    kicker: "Chapter 1 · why reviews matter more than ads",
    title: "Reviews are the single strongest signal of business trust.",
    body: "Every year, ACCC surveys keep confirming the same thing: 88% of Australians read reviews before deciding to book a restaurant, hire a tradie or trust a new local business. But the way most businesses currently collect reviews is broken. Google reviews are hostage to Google's algorithm — a single competitor's fake one-star can tank your local ranking for months, and the review-flagging process is a black box. TripAdvisor charges $500/month for the privilege of managing your listing. Zomato disappeared overnight for half its restaurants in Australia in 2020. Every one of these platforms treats your review corpus as their asset — not yours. HustleReviews inverts this. Every review lives at your HustleDirectory profile, backed by your ABN, portable to CSV, and syndicated to any surface you choose. The reviewers are ABN-verified — real HustleSuite accounts, not anonymous burner emails. Disputes are reviewed by a real human within 48 hours. And it's free forever for every Australian business, because we make our money when your Suite grows, not when we hold your reputation hostage.",
    img: IMG_STARS,
  },
  {
    kicker: "Chapter 2 · one-tap post-visit review requests",
    title: "Turn a visit into a review with a single tap.",
    body: "The single biggest gap in most review platforms is the ask. Google won't send review requests for you. TripAdvisor's email tool has a 1.4% response rate industry-wide. HustleReviews is built around the one-tap post-visit review request. The moment a diner walks out, a HustlePlate booking is marked completed, a HustleEats seat is served, or a HustleStay guest checks out, the venue owner sees a green 'Send review request' button on the transaction. One tap. The recipient receives a personalised email or SMS (their choice) with your logo, the visit date, and a one-tap star form. No login, no friction, no eight-field verification captcha. Industry-standard response rate for this kind of transactional request is 12-18% — orders of magnitude above cold-email review requests. And every review is auto-linked to the underlying transaction, so a HustleReviews entry can't be faked or gamed.",
    img: IMG_RESPONSE,
  },
  {
    kicker: "Chapter 3 · reviews you own, forever",
    title: "Your review corpus is a first-party asset.",
    body: "The magic of HustleReviews is that your reviews behave like a real asset — not a walled-garden liability. Every review is exportable to CSV any time from your inbox. Delete your HustleSuite account and we hand back the entire review corpus in a downloadable .zip within 30 days. Reviews render on your website via the HustleConnect widget (or a direct embed) with schema.org LocalBusiness JSON-LD, so Google surfaces your five-star rating in the SERP snippet. And because reviews are linked to your ABN, not to the HustleSuite domain, you can carry them to any successor platform if you ever leave. This is not a hypothetical — a review corpus is the single most valuable long-term marketing asset a small business builds. Treating it as your asset (not ours) is the founding rule of HustleReviews.",
    img: IMG_HERO,
  },
  {
    kicker: "Chapter 4 · public replies + private DMs",
    title: "The right response, on the right surface.",
    body: "Some reviews deserve a public reply — the four-star that says 'wait times were long but the food was excellent' gives you a chance to acknowledge the wait and show your care in public. But some reviews deserve a private conversation — the one-star that misidentifies your service, the customer with a genuine allergen complaint, the couple asking for a refund. HustleReviews gives you both. Every review has a public 'Reply as owner' button that publishes your response next to the review with an owner badge. And every review has a 'Take it to DMs' button that opens a private HustleCRM thread with the reviewer — visible only to you and them, fully logged, exportable. This is the mechanic that turns your review inbox from a source of stress into a genuine customer-service channel. Owners who respond to reviews within 24 hours see a 34% higher return rate compared to owners who never respond, according to the HustleReviews internal cohort data across 400+ Brisbane venues.",
    img: IMG_WIDGET,
  },
  {
    kicker: "Chapter 5 · rich-result markup + the SEO compound",
    title: "Google surfaces your five-star rating on the SERP.",
    body: "Every HustleReviews entry ships with schema.org Review + AggregateRating JSON-LD attached to your directory profile, your HustlePlate booking page, and your embedded website widget. When Google's crawler picks up the markup, your business becomes eligible for the SERP star-rating rich result — that visual gold star and average rating shown next to your business name in the search results. Over 90 days of building a review corpus, this markup compounds into first-page ranking for '{your business} + reviews' searches, and improves the click-through rate on branded searches by up to 40%. The HustleReviews team also pushes IndexNow ping-outs to Bing / Yandex / DuckDuckGo on every new review — those crawlers re-crawl within hours. Compare this to Google Reviews, where the same rich-result markup lives inside Google's walled garden and doesn't help your organic search ranking at all.",
    img: IMG_STARS,
  },
  {
    kicker: "Chapter 6 · disputes handled by real humans",
    title: "Fake reviews don't stand. Real complaints get heard.",
    body: "Every review platform has a review-dispute mechanism, but almost every one is a black-box AI process where you flag a review, get an automated 'we've reviewed this' email 3 days later, and never see the review disappear. HustleReviews works differently. Every dispute is reviewed by a HustleSuite trust & safety human within 48 hours. If the reviewer's ABN-linked account fails verification (fake identity, sock-puppet account, competitor-owned account, review-farm signature), the review is removed and the reviewing account is suspended. If the review is genuine but the complaint is procedural (allergen misidentification, refund pending), we mediate — private HustleCRM thread with both sides, resolution logged, and a public follow-up appended to the original review with both parties' consent. This mechanic is expensive but critical: it means your review corpus stays a genuine, defensible asset, not a fake-review battlefield. Every business owner can request an escalation to a senior mediator; every senior mediator has published a real name and a real bio on the HustleReviews trust page.",
    img: IMG_RESPONSE,
  },
];

const TESTIMONIALS = [
  { name: "Elena, Bulimba Bakehouse", quote: "The one-tap post-visit request is the game-changer. My previous review platform's request tool had a 2% response rate. HustleReviews gets me 17% and every review is verified. My aggregate rating went from 3.9 to 4.6 in six months." },
  { name: "Tom, Woolloongabba Auto", quote: "A competitor's ex-employee left me three fake one-stars last year. HustleReviews' trust team removed all three within 48 hours — with an actual explanation. My old platform never even replied." },
  { name: "Ash, Sunshine Coast Spa", quote: "The website widget with schema markup put my average rating into Google's SERP snippet. My call volume from Google searches went up 40% in three months. Nothing else in my funnel moved." },
];

const FAQS = [
  { q: "Is HustleReviews really free forever?",
    a: "Yes. Unlimited verified reviews, unlimited public responses, unlimited private DMs, 100 review request emails per month, CSV export any time — all free forever for every ABN-verified Australian business. Featured is a $29/month add-on that unlocks unlimited review request emails, SMS requests, the embeddable website widget, and schema markup." },
  { q: "How is this different from Google Reviews?",
    a: "Google owns your Google reviews. HustleReviews puts you in control: every review lives at your ABN-linked directory profile, exportable to CSV any time, portable to any successor platform, and syndicated to your website via a widget. Disputes are reviewed by real humans in 48h. Zero platform lock-in." },
  { q: "How do you stop fake reviews?",
    a: "Every reviewer is ABN-linked to a real HustleSuite account, verified against the Australian Business Register (for business reviewers) or the transaction history (for diners / customers). Review-farm patterns are auto-flagged. Every disputed review is reviewed by a real human trust & safety mediator within 48 hours." },
  { q: "Can I import my existing reviews from Google / TripAdvisor / Zomato?",
    a: "Yes — we support bulk import via CSV upload. Imported reviews are marked with an 'imported' badge (with the source platform) so future readers know the provenance. Your imported reviews are still your asset, but we don't fabricate verification we can't provide." },
  { q: "What happens if I stop paying for Featured?",
    a: "You lose the unlimited review request emails, SMS requests, embeddable widget, and schema markup — but all your existing reviews stay live at your directory profile forever. HustleReviews is deliberately not designed to punish you for downgrading." },
  { q: "How does this play with HustleDirectory + HustlePlate?",
    a: "HustleReviews is the review layer. HustleDirectory is your public profile. HustlePlate is your operating layer. All three share a single sub-account inside HustleCRM — so a review, a directory edit, and a booking all land in the same inbox with a unified customer identity." },
];

// Render helpers
function TierCard({ tier }) {
  return (
    <div data-testid={`reviews-tier-${tier.key}`}
         style={{
           background: "#fff", borderRadius: 18, padding: "26px 24px",
           border: tier.highlight ? "2px solid #b08a3e" : "1px solid #ecdeb7",
           boxShadow: tier.highlight ? "0 20px 44px rgba(176,138,62,.22)" : "0 8px 20px rgba(0,0,0,.06)",
           position: "relative", display: "flex", flexDirection: "column", gap: 14,
         }}>
      {tier.badge && (
        <span style={{
          position: "absolute", top: -12, right: 18,
          background: "linear-gradient(135deg,#1f1f1f 0%,#4a3b1f 50%,#b08a3e 100%)",
          color: "#fff", fontSize: 11, fontWeight: 800,
          padding: "5px 12px", borderRadius: 999, letterSpacing: ".05em",
        }}>{tier.badge}</span>
      )}
      <div>
        <div style={{ color: tier.color, fontWeight: 800, letterSpacing: ".12em",
                       fontSize: 12, textTransform: "uppercase" }}>{tier.name}</div>
        <div style={{ marginTop: 6, fontFamily: "Georgia,serif",
                       fontSize: 36, color: "#1a1408" }}>
          {tier.price}
          <span style={{ fontSize: 15, color: "#8a7a4a", marginLeft: 4 }}>{tier.period}</span>
        </div>
      </div>
      <p style={{ color: "#5a4a1a", lineHeight: 1.55, fontSize: 14, margin: 0 }}>{tier.blurb}</p>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
        {tier.features.map((f, i) => (
          <li key={i} style={{ display: "flex", gap: 8, fontSize: 14, color: "#1a1408", lineHeight: 1.45 }}>
            <Check size={16} color={tier.color} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link to={tier.cta.href} data-testid={`reviews-tier-${tier.key}-cta`}
            style={{ ...ctaPrimaryStyle, alignSelf: "flex-start", marginTop: 4,
                       background: tier.highlight ? "#b08a3e" : "#c2410c",
                       boxShadow: tier.highlight
                         ? "0 8px 20px rgba(176,138,62,.32)"
                         : "0 8px 20px rgba(194,65,12,.22)" }}>
        {tier.cta.label} <ArrowRight size={16} />
      </Link>
    </div>
  );
}

function StatPill({ num, label }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "baseline", gap: 6,
      padding: "8px 14px", borderRadius: 999,
      background: "#fff", border: "1px solid #ecdeb7",
      boxShadow: "0 4px 10px rgba(0,0,0,.05)",
    }}>
      <strong style={{ fontFamily: "Georgia,serif", fontSize: 20, color: "#1a1408" }}>{num}</strong>
      <span style={{ fontSize: 13, color: "#5a4a1a" }}>{label}</span>
    </div>
  );
}

const PrintStyles = () => (
  <style>{`
    @media print {
      [data-testid="reviews-print-btn"], [data-hp-founder-chat] { display: none !important; }
      body { background: white !important; }
    }
  `}</style>
);

export default function HustleReviewsSalesPage() {
  useEffect(() => {
    document.title = "HustleReviews — Reviews you own, forever.";
  }, []);

  return (
    <div data-testid="hustlereviews-sales-page" className="min-h-screen"
         style={{ background: "#fdf9ec", color: "#1a1408" }}>
      <PrintStyles />
      <PlatformHeader salesTopGap
        brand={{ name: "HustleReviews", to: "/" }}
        nav={[
          { to: "/reviews", label: "Write a review" },
          { to: "#tiers", label: "Pricing" },
          { to: "/claim-business?product=reviews", label: "Claim my business", align: "right", variant: "primary" },
        ]}
        showBookCta={false}
        showCart={false}
      />

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 24px 80px" }}>

        {/* Hero */}
        <section data-testid="reviews-hero" style={{ padding: "40px 0 24px" }}>
          <img src={LOGO} alt="HustleReviews" style={{ height: 52, marginBottom: 14 }} />
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 10px" }}>
            HustleReviews · portable + verified
          </p>
          <h1 style={{ fontFamily: "Georgia,serif", fontSize: 46, lineHeight: 1.1,
                        color: "#1a1408", margin: "0 0 14px", maxWidth: 900 }}>
            Reviews you{" "}
            <em style={{ color: "#b08a3e", fontStyle: "italic" }}>own</em>, forever.
          </h1>
          <p style={{ fontSize: 17, color: "#5a4a1a", maxWidth: 820,
                       margin: "0 0 20px", lineHeight: 1.55 }}>
            HustleReviews is the portable, ABN-verified review layer for Australian
            businesses. Every reviewer is a real HustleSuite account. Every review
            renders on your website with schema.org rich-result markup. Fake reviews
            are reviewed by a real human in 48h.{" "}
            <strong>Free forever for every ABN-verified business.</strong>
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 22 }}>
            <StatPill num="ABN" label="verified reviewers" />
            <StatPill num="48h" label="dispute-review SLA" />
            <StatPill num="Free" label="unlimited reviews" />
            <StatPill num="CSV" label="portable export any time" />
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link to="/claim-business?product=reviews"
                  data-testid="reviews-cta-claim" style={ctaPrimaryStyle}>
              Claim my business <ArrowRight size={16} />
            </Link>
            <Link to="/reviews" data-testid="reviews-cta-write" style={ctaSecondaryStyle}>
              <Star size={16} /> Write a review
            </Link>
            <button data-testid="reviews-print-btn"
                    onClick={() => window.print()}
                    style={{ ...ctaSecondaryStyle, cursor: "pointer" }}>
              <Printer size={16} /> Print proposal
            </button>
          </div>

          <img src={IMG_HERO} alt="Happy customers writing a review"
               style={{ marginTop: 32, width: "100%", height: 340,
                         objectFit: "cover", borderRadius: 18,
                         border: "1px solid #ecdeb7",
                         boxShadow: "0 20px 44px rgba(0,0,0,.08)" }} />
        </section>

        {/* Capabilities */}
        <section style={{ marginTop: 64 }} data-testid="reviews-caps">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 0 · what ships in the box
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Six capabilities in one review layer.
          </h2>
          <div style={{ display: "grid", gap: 20,
                         gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {CAPS.map((c, i) => (
              <div key={i} data-testid={`reviews-cap-${i}`}
                   style={{ background: "#fff", borderRadius: 16, padding: "22px 22px",
                              border: "1px solid #ecdeb7",
                              boxShadow: "0 8px 20px rgba(0,0,0,.05)" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10,
                               background: "#fff8e6", color: "#b08a3e",
                               display: "grid", placeItems: "center", marginBottom: 12 }}>
                  <c.icon size={22} />
                </div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 18,
                              color: "#1a1408", margin: "0 0 6px" }}>{c.t}</h3>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.5, margin: 0 }}>{c.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 6 chapters with images */}
        <section style={{ marginTop: 72 }} data-testid="reviews-chapters">
          {CHAPTERS.map((ch, i) => (
            <article key={i} data-testid={`reviews-chapter-${i}`}
                     style={{ display: "grid",
                               gridTemplateColumns: i % 2 ? "1fr 320px" : "320px 1fr",
                               gap: 32, alignItems: "center",
                               marginBottom: 56 }}>
              <img src={ch.img} alt={ch.title}
                   style={{ width: "100%", height: 240, objectFit: "cover",
                             borderRadius: 14, border: "1px solid #ecdeb7",
                             order: i % 2 ? 2 : 1 }} />
              <div style={{ order: i % 2 ? 1 : 2 }}>
                <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                             color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>{ch.kicker}</p>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 28,
                              color: "#1a1408", margin: "0 0 12px", lineHeight: 1.2 }}>{ch.title}</h3>
                <p style={{ fontSize: 15, color: "#5a4a1a", lineHeight: 1.7, margin: 0 }}>{ch.body}</p>
              </div>
            </article>
          ))}
        </section>

        {/* How it works */}
        <section style={{ marginTop: 72 }} data-testid="reviews-how">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 7 · go live in three steps
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Claim → Request → Respond.
          </h2>
          <div style={{ display: "grid", gap: 18,
                         gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {HOW.map((s) => (
              <div key={s.n} data-testid={`reviews-step-${s.n}`}
                   style={{ background: "#fff", borderRadius: 16, padding: "24px 24px",
                              border: "1px solid #ecdeb7",
                              display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontFamily: "Georgia,serif", fontSize: 34,
                                    color: "#b08a3e", lineHeight: 1 }}>{s.n}</span>
                  <s.icon size={20} color="#c2410c" />
                </div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 20,
                              color: "#1a1408", margin: 0 }}>{s.t}</h3>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55, margin: 0 }}>{s.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Tiers */}
        <section id="tiers" style={{ marginTop: 72 }} data-testid="reviews-tiers">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 8 · pricing
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 10px" }}>
            Free forever. Featured when you're ready to grow.
          </h2>
          <p style={{ fontSize: 15, color: "#5a4a1a", maxWidth: 820, lineHeight: 1.55, margin: "0 0 28px" }}>
            Every ABN-verified Australian business gets unlimited HustleReviews for
            free forever. Featured unlocks unlimited review request emails, SMS
            requests, the embeddable website widget, and schema.org rich-result
            markup. Multi-location groups get bespoke pricing.
          </p>
          <div style={{ display: "grid", gap: 20,
                         gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))" }}>
            {TIERS.map((t) => <TierCard key={t.key} tier={t} />)}
          </div>
        </section>

        {/* Testimonials */}
        <section style={{ marginTop: 72 }} data-testid="reviews-testimonials">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 9 · business owners on HustleReviews
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            The people running it, in their words.
          </h2>
          <div style={{ display: "grid", gap: 18,
                         gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {TESTIMONIALS.map((tm, i) => (
              <blockquote key={i} data-testid={`reviews-testimonial-${i}`}
                          style={{ background: "#fff", borderRadius: 16,
                                     padding: "22px 22px", border: "1px solid #ecdeb7",
                                     boxShadow: "0 8px 20px rgba(0,0,0,.05)", margin: 0 }}>
                <Sparkles size={18} color="#b08a3e" style={{ marginBottom: 8 }} />
                <p style={{ fontSize: 15, color: "#1a1408", lineHeight: 1.55,
                             margin: "0 0 12px", fontStyle: "italic" }}>"{tm.quote}"</p>
                <footer style={{ fontSize: 12, letterSpacing: ".16em",
                                    textTransform: "uppercase", color: "#b08a3e",
                                    fontWeight: 800 }}>— {tm.name}</footer>
              </blockquote>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section style={{ marginTop: 72 }} data-testid="reviews-faq">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 10 · honest FAQ
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Questions business owners actually ask.
          </h2>
          <div style={{ display: "grid", gap: 12 }}>
            {FAQS.map((f, i) => (
              <details key={i} data-testid={`reviews-faq-${i}`}
                       style={{ background: "#fff", borderRadius: 12,
                                  border: "1px solid #ecdeb7", padding: "16px 20px" }}>
                <summary style={{ fontFamily: "Georgia,serif", fontSize: 17,
                                    color: "#1a1408", cursor: "pointer",
                                    fontWeight: 600 }}>{f.q}</summary>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55,
                             margin: "10px 0 0" }}>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section style={{ marginTop: 72 }} data-testid="reviews-final-cta">
          <div style={{
            background: "linear-gradient(135deg,#1a1408 0%,#4a3b1f 55%,#b08a3e 100%)",
            color: "#fff", padding: "40px 36px", borderRadius: 22,
            display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center",
          }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <h2 style={{ fontFamily: "Georgia,serif", fontSize: 30, margin: "0 0 8px" }}>
                Own your reviews. For life.
              </h2>
              <p style={{ fontSize: 15, opacity: .9, margin: 0, lineHeight: 1.55 }}>
                Two minutes to claim. Zero card. Unlimited verified reviews forever.
                Portable to CSV any time. If you ever leave, you keep every byte.
              </p>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Link to="/claim-business?product=reviews"
                    data-testid="reviews-final-claim"
                    style={{ ...ctaPrimaryStyle, background: "#fff", color: "#1a1408",
                              boxShadow: "0 10px 30px rgba(0,0,0,.28)" }}>
                Claim my business <ArrowRight size={16} />
              </Link>
              <Link to="/claim-business?product=reviews&tier=featured"
                    data-testid="reviews-final-featured"
                    style={{ ...ctaSecondaryStyle, borderColor: "#fff", color: "#fff" }}>
                <Building2 size={16} /> Talk about Featured
              </Link>
            </div>
          </div>
        </section>

      </main>

      <FounderChatPill testid="reviews-founder-chat" />
      <SuiteFooter />
    </div>
  );
}
